import InfoIcon from 'components/ReusableComponents/Icons/Info'
import { useEffect, useRef, useState } from 'react'
import announcementApi from 'services/api/announcement'

import { inPropertyAudiences } from '../constants'
import styles from './AudienceInfoBar.module.scss'
import {
  getNotEligibleCount,
  getNotEligibleReasons
} from './getNotEligibleReasons'
import { shouldFetchEligibilityReasons } from './shouldFetchEligibilityReasons'

const AudienceInfoBar = ({ audience, audienceSize, totalEligibleForText }) => {
  const audienceData = inPropertyAudiences[audience]
  const rootRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [reasonsData, setReasonsData] = useState(null)

  useEffect(() => {
    setIsOpen(false)
    setIsLoading(false)
    setError(null)
    setReasonsData(null)
  }, [audience])

  useEffect(() => {
    const handlePointerDown = event => {
      if (!isOpen || rootRef.current?.contains(event.target)) {
        return
      }

      setIsOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [isOpen])

  if (!audienceData) {
    return null
  }

  const Icon = audienceData?.icon
  const title = audienceData?.name
  const showEligibilityBreakdown =
    audienceSize !== 0 && totalEligibleForText < audienceSize
  const notEligibleReasons = getNotEligibleReasons(reasonsData)
  // Keep tooltip total aligned with headline: eligible + notEligible === audienceSize
  const notEligibleCount =
    audienceSize != null && totalEligibleForText != null
      ? Math.max(audienceSize - totalEligibleForText, 0)
      : getNotEligibleCount(reasonsData)

  async function handleToggleBreakdown() {
    if (isOpen) {
      setIsOpen(false)
      return
    }

    setIsOpen(true)

    if (
      !shouldFetchEligibilityReasons({
        isOpening: true,
        hasCachedData: Boolean(reasonsData),
        isLoading
      })
    ) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await announcementApi.getEligibilityReasons({ audience })
      setReasonsData(data)
    } catch (e) {
      setError('Unable to load not-eligible details')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className={styles.icon}>
        <Icon />
      </div>
      <div className={styles.info}>
        <p className={styles.name}>{title}</p>
        <p className={styles.description}>
          {audienceSize === 0 && <>No eligable guests</>}
          {audienceSize !== 0 && totalEligibleForText === audienceSize && (
            <>{totalEligibleForText} guests are eligible to text</>
          )}
          {showEligibilityBreakdown && (
            <>
              {totalEligibleForText} of {audienceSize} guests are eligible to text{' '}
              <span className={styles.tooltipHolder} ref={rootRef}>
                <button
                  type="button"
                  className={styles.infoButton}
                  aria-expanded={isOpen}
                  aria-label="Show not eligible breakdown"
                  onClick={handleToggleBreakdown}
                >
                  <InfoIcon className={styles.infoIcon} />
                </button>
                {isOpen && (
                  <div className={styles.tooltip} role="tooltip">
                    <p className={styles.tooltipTitle}>
                      {notEligibleCount > 0
                        ? `${notEligibleCount} not eligible`
                        : 'Not eligible'}
                    </p>
                    {isLoading && (
                      <p className={styles.tooltipContent}>Loading...</p>
                    )}
                    {!isLoading && error && (
                      <p className={styles.tooltipContent}>{error}</p>
                    )}
                    {!isLoading && !error && notEligibleReasons.length > 0 && (
                      <ul className={styles.reasonsList}>
                        {notEligibleReasons.map(reason => (
                          <li
                            key={reason.code ?? reason.Code}
                            className={styles.reasonRow}
                          >
                            <span className={styles.reasonLabel}>
                              {reason.label ?? reason.Label}
                            </span>
                            <span className={styles.reasonCount}>
                              {reason.count ?? reason.Count}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {!isLoading &&
                      !error &&
                      reasonsData &&
                      notEligibleReasons.length === 0 && (
                        <p className={styles.tooltipContent}>
                          No not-eligible details
                        </p>
                      )}
                  </div>
                )}
              </span>
            </>
          )}
        </p>
      </div>
    </>
  )
}

export default AudienceInfoBar
